<template>
    <div class="div-calculator">
        <div v-for="(c, i) in grouped_components.filter(comp => !comp.omit_in_composition)"
             v-if="(c.name + c.cas_number).trim().toLowerCase().includes(name.trim().toLowerCase())"
             class="mb-2"
        >
            <b-row>
                <b-col>
                    <span class="font-weight-bold">
                        <b-badge variant="success"
                                 class="mr-1"
                                 style="width: 90px"
                        >
                            {{ c.cas_number }}
                        </b-badge>
                        {{ c.name }}
                    </span>
                    <span class="font-weight-bold float-right">
                        <b-badge variant="primary"
                                 style="width: 90px"
                        >
                            {{
                                Math.round(c.final_percentage * 10000) / 10000
                            }}%
                        </b-badge>
                    </span>
                    <hr class="mt-1 mb-2">
                </b-col>
            </b-row>

            <form @submit.prevent="validate(i)"
                  :data-vv-scope="'toxicity' + i"
            >
                <b-row>
                    <b-col md
                           cols="12"
                    >
                        <label for="dl_50_oral">
                            ORAL DL50 <span class="text-black-50 small">(mg/kg)</span>

                            <validation_tooltip
                                field="dl_50_oral"
                                :value="c"
                            />
                        </label>
                        <b-input-group class="mb-2">
                            <b-input-group-prepend is-text>
                                <el-tooltip effect="dark"
                                            placement="top"
                                            popper-class="py-1 px-2"
                                >
                                    <div slot="content">
                                        Marque esta casilla si sabe que este componente NO <br/>
                                        presenta toxicidad aguda por esta vía de exposición
                                    </div>
                                    <b-form-checkbox v-model="c.toxicity.add_dl_50_oral"
                                                     :unchecked-value="0"
                                                     :value="1"
                                                     @change="setChanged(c)"
                                                     :disabled="only_view"
                                    />
                                </el-tooltip>
                            </b-input-group-prepend>
                            <b-form-input :id="'dl_50_oral_' + i"
                                          :name="'dl_50_oral_' + i"
                                          size="sm"
                                          type="number"
                                          v-model="c.toxicity.dl_50_oral"
                                          data-vv-as=" "
                                          v-validate="'decimal:7|min_value:0|max_value:100000'"
                                          @change="setChanged(c)"
                            ></b-form-input>
                        </b-input-group>
                        <span class="text-danger small"
                              v-text="errors.first('toxicity' + i + '.dl_50_oral_' + i)"
                        />
                    </b-col>

                    <b-col md
                           cols="12"
                    >
                        <label for="dl_50_cutaneous">
                            CUTÁNEA DL50 <span class="text-black-50 small">(mg/kg)</span>
                            <validation_tooltip
                                field="dl_50_cutaneous"
                                :value="c"
                            />
                        </label>
                        <b-input-group class="mb-2">
                            <b-input-group-prepend is-text>
                                <el-tooltip effect="dark"
                                            placement="top"
                                            popper-class="py-1 px-2"
                                >
                                    <div slot="content">
                                        Marque esta casilla si sabe que este componente NO <br/>
                                        presenta toxicidad aguda por esta vía de exposición
                                    </div>
                                    <b-form-checkbox v-model="c.toxicity.add_dl_50_cutaneous"
                                                     :unchecked-value="0"
                                                     :value="1"
                                                     @change="setChanged(c)"
                                                     :disabled="only_view"
                                    />
                                </el-tooltip>
                            </b-input-group-prepend>
                            <b-form-input :id="'dl_50_cutaneous_' + i"
                                          :name="'dl_50_cutaneous_' + i"
                                          size="sm"
                                          type="number"
                                          v-model="c.toxicity.dl_50_cutaneous"
                                          data-vv-as=" "
                                          v-validate="'decimal:7|min_value:0|max_value:100000'"
                                          @change="setChanged(c)"
                            ></b-form-input>
                        </b-input-group>
                        <span class="text-danger small"
                              v-text="errors.first('toxicity' + i + '.dl_50_cutaneous_' + i)"
                        />
                    </b-col>

                    <b-col md="5"
                           cols="12"
                    >
                        <label for="cl_50_inhalation">
                            INHALACIÓN CL50
                            <validation_tooltip
                                field="cl_50_inhalation"
                                :value="c"
                            />
                        </label>
                        <b-row>
                            <b-col class="pr-1">
                                <b-input-group class="mb-2">
                                    <b-input-group-prepend is-text>
                                        <el-tooltip effect="dark"
                                                    placement="top"
                                                    popper-class="py-1 px-2"
                                        >
                                            <div slot="content">
                                                Marque esta casilla si sabe que este componente NO <br/>
                                                presenta toxicidad aguda por esta vía de exposición
                                            </div>
                                            <b-form-checkbox v-model="c.toxicity.add_cl_50_inhalation"
                                                             :unchecked-value="0"
                                                             :value="1"
                                                             @change="setChanged(c)"
                                                             :disabled="only_view"
                                            />
                                        </el-tooltip>
                                    </b-input-group-prepend>
                                    <b-form-input :id="'cl_50_inhalation_' + i"
                                                  :name="'cl_50_inhalation_' + i"
                                                  size="sm"
                                                  type="number"
                                                  v-model="c.toxicity.cl_50_inhalation"
                                                  data-vv-as=" "
                                                  v-validate="{rules:{required: !!c.toxicity.sys_measure_unit_cl_50_id, decimal:4, min_value:0, max_value:100000}}"
                                                  @change="setChanged(c)"
                                    ></b-form-input>
                                </b-input-group>
                                <span class="text-danger small"
                                      v-text="errors.first('toxicity' + i + '.cl_50_inhalation_' + i)"
                                />
                            </b-col>
                            <b-col class="pl-1">
                                <el-select :id="'sys_measure_unit_cl_50_id_' + i"
                                           :name="'sys_measure_unit_cl_50_id_' + i"
                                           v-model="c.toxicity.sys_measure_unit_cl_50_id"
                                           size="small"
                                           clearable
                                           no-data-text="No hay datos que coincidan"
                                           no-match-text="No hay datos que coincidan"
                                           data-vv-as=" "
                                           v-validate="{rules:{required: !!c.toxicity.cl_50_inhalation}}"
                                           @change="setChanged(c)"
                                           disabled
                                >
                                    <el-option v-for="measure_unit in measureUnitsCl50"
                                               :key="measure_unit.id"
                                               :value="measure_unit.id"
                                               :label="measure_unit.lista"
                                    />
                                </el-select>
                                <span class="text-danger small"
                                      v-text="errors.first('toxicity' + i + '.sys_measure_unit_cl_50_id_' + i)"
                                />
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </form>
        </div>

        <b-row>
            <b-col cols="12"
                   class="text-center mb-2 mt-1"
            >
                <b-btn variant="success"
                       :disabled="!grouped_components.some(c => c.changed)"
                       size="sm"
                       @click="saveAll()"
                >
                    <i class="fa fa-save"/>
                    <span v-if="grouped_components.some(c => c.changed)">
                        Guardar <b-badge variant="light"> {{ grouped_components.filter(comp => comp.changed).length }}</b-badge> componente(s) modificado(s)
                    </span>
                    <span v-else>
                        Guardar componentes
                    </span>
                </b-btn>
            </b-col>
        </b-row>
    </div>
</template>

<script>

import {mapActions, mapMutations, mapState, mapGetters} from 'vuex';
import Classification from '../../../components/classification.vue';
import validation_tooltip from "../../../components/validation_tooltip.vue";

export default {
    name: 'index',
    components: {
        Classification,
        validation_tooltip
    },
    props: {
        name: {
            required: true,
            default: ''
        }
    },
    computed: {
        ...mapState('calculator', [
            'measure_units_cl_50',
            'grouped_components',
            'only_view',
            'product',
            'totals',
            'max'
        ]),
        ...mapGetters('calculator', ['measureUnitsCl50'])
    },
    methods: {
        ...mapActions('calculator', [
            'downloadClassificationAcuteToxicity',
            'getGroupedComponents',
            'updateToxicitiesComponents',
            'getClassification',
            'updateToxicitiesComponents'
        ]),
        ...mapMutations('calculator', [
            'setGroupedComponents',
            'setModalToxicityShow'
        ]),
        async saveAll() {
            let valid = await this.validate();

            if (valid) {
                let components = this.grouped_components.filter(c => c.changed);

                await this.updateToxicitiesComponents({components});

                this.$emit('getClassifications');
            }
        },
        async validate(index = 0) {
            if (index >= this.grouped_components.length) {
                return true;
            }

            let valid = true;
            let second_valid = true;
            let component = this.grouped_components[index];

            if (component.changed){
                valid = await this.$validator.validateAll('toxicity' + index);
            }

            second_valid = await this.validate(index + 1);

            return valid && second_valid;
        },
        setChanged(component_product, changed = true) {
            const updatedComponents = this.grouped_components.map(gc =>
                gc.id === component_product.id ? {...gc, changed} : gc
            );
            this.setGroupedComponents(updatedComponents);

            this.setUnit(component_product);
        },
        setUnit(component) {
            if (component.toxicity.cl_50_inhalation) {
                const unit = this.measureUnitsCl50[0];
                component.toxicity.sys_measure_unit_cl_50_id = unit.id;
            } else {
                component.toxicity.sys_measure_unit_cl_50_id = null;
            }
        }
    }
};
</script>
